// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.
import { create } from 'zustand';
import type { Channel, Member, Server, ServerEvent } from '@/models';

export interface ServerState {
  server: Server | null;
  events: ServerEvent[];
  members: Member[];

  setServer: (server: Server) => void;
  setEvents: (events: ServerEvent[]) => void;
  setMembers: (members: Member[]) => void;

  updateChannels: (
    channels: Channel[] | ((prev: Channel[]) => Channel[])
  ) => void;
  updateEvents: (
    events: ServerEvent[] | ((prev: ServerEvent[]) => ServerEvent[])
  ) => void;
  updateMembers: (members: Member[] | ((prev: Member[]) => Member[])) => void;

  clear: () => void;

  addChannel: (channel: Channel) => void;
  addEvent: (event: ServerEvent) => void;
  addMember: (member: Member) => void;
}

export const useServerStore = create<ServerState>((set) => ({
  server: null,
  events: [],
  members: [],

  setServer: (server) => set({ server }),
  setEvents: (events) => set({ events }),
  setMembers: (members) => set({ members }),

  updateChannels: (updater) =>
    set((state) => {
      const prevChannels = state.server?.channels || [];
      const newChannels =
        typeof updater === 'function' ? updater(prevChannels) : updater;
      return state.server
        ? { server: { ...state.server, channels: newChannels } }
        : state;
    }),
  updateEvents: (updater) =>
    set((state) => {
      const prevEvents = state.server?.serverEvents || [];
      const newEvents =
        typeof updater === 'function' ? updater(prevEvents) : updater;
      return state.server
        ? { server: { ...state.server, serverEvents: newEvents } }
        : state;
    }),
  updateMembers: (updater) =>
    set((state) => {
      const prevMembers = state.server?.members || [];
      const newMembers =
        typeof updater === 'function' ? updater(prevMembers) : updater;
      return state.server
        ? { server: { ...state.server, members: newMembers } }
        : state;
    }),

  clear: () => set({ server: null, events: [] }),

  addChannel: (channel) =>
    set((state) =>
      state.server
        ? {
            server: {
              ...state.server,
              channels: [...(state.server.channels || []), channel],
            },
          }
        : state
    ),
  addEvent: (event) => set((state) => ({ events: [...state.events, event] })),
  addMember: (member) =>
    set((state) => ({ members: [...state.members, member] })),
}));
